import React from "react";
import { createPortal } from "react-dom";
import { BACK_TO_TOP_THRESHOLD } from "./BackToTop";

// Save confirmation for when the page header -- and the "All changes saved"
// chip in it -- has scrolled out of view. At the top of the page the chip
// already says this, so the toast stays hidden there rather than doubling up.
//
// Driven entirely by the page's existing save state machine
// (idle -> saving -> saved | error). It never claims a save that did not
// succeed: "saved" only appears after saveData resolves.
const SAVED_VISIBLE_MS = 2600;

// Measured synchronously at the moment a save resolves. IntersectionObserver
// and the rAF scroll handler both deliver a frame late, so a save that completes
// right after a scroll would otherwise be judged against the header's PREVIOUS
// position -- and the "saved" confirmation would be suppressed as if the user
// could still see the chip. Two rect reads per save is cheap.
function measure(anchor) {
  const root = anchor?.closest(".content");
  if (!anchor || !root) return null;
  const a = anchor.getBoundingClientRect();
  const r = root.getBoundingClientRect();
  return {
    anchorVisible: a.bottom > r.top && a.top < r.bottom,
    raised: root.scrollTop > BACK_TO_TOP_THRESHOLD,
  };
}

export default function SaveToast({ status, anchorRef }) {
  const [anchorVisible, setAnchorVisible] = React.useState(true);
  const [raised, setRaised] = React.useState(false);
  const [toast, setToast] = React.useState(null); // null | "saving" | "saved" | "error"
  const anchorVisibleRef = React.useRef(true);
  const prevStatus = React.useRef(status);
  const hideTimer = React.useRef(0);

  React.useEffect(() => {
    const anchor = anchorRef?.current;
    const root = anchor?.closest(".content") || null;
    if (!anchor || !root) return undefined;

    let io = null;
    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        ([entry]) => {
          const isVisible = !!entry?.isIntersecting;
          anchorVisibleRef.current = isVisible;
          setAnchorVisible(isVisible);
        },
        { root, threshold: 0 },
      );
      io.observe(anchor);
    }

    // Sit above the back-to-top button whenever that button is showing.
    let frame = 0;
    const update = () => {
      frame = 0;
      setRaised(root.scrollTop > BACK_TO_TOP_THRESHOLD);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    root.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      io?.disconnect();
      root.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [anchorRef]);

  React.useEffect(() => {
    const prev = prevStatus.current;
    prevStatus.current = status;
    if (status === prev) return;

    const now = measure(anchorRef?.current);
    if (now) {
      anchorVisibleRef.current = now.anchorVisible;
      setAnchorVisible(now.anchorVisible);
      setRaised(now.raised);
    }

    if (status === "saved") {
      window.clearTimeout(hideTimer.current);
      // Only latch a success the user could not already see in the header.
      if (anchorVisibleRef.current) {
        setToast(null);
        return;
      }
      setToast("saved");
      hideTimer.current = window.setTimeout(() => {
        setToast((t) => (t === "saved" ? null : t));
      }, SAVED_VISIBLE_MS);
    } else if (status === "error") {
      // A failure stays up until a later save succeeds -- it must not quietly
      // time out while the user's changes are still unsaved.
      window.clearTimeout(hideTimer.current);
      setToast("error");
    } else if (status === "saving") {
      // Relabel an error that is already showing; never pop a toast on every
      // keystroke from idle.
      setToast((t) => (t === "error" ? "saving" : t));
    }
  }, [status, anchorRef]);

  React.useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  const show = !!toast && !anchorVisible;
  const kind = toast || "saved";
  const label =
    kind === "error"
      ? "Couldn't save your changes"
      : kind === "saving"
        ? "Saving…"
        : "Settings saved";

  return createPortal(
    <div
      className={`saveToast is-${kind}${show ? " isVisible" : ""}${raised ? " isRaised" : ""}`}
      role="status"
      aria-live="polite"
    >
      {show ? (
        <>
          <span className="saveToast__icon" aria-hidden="true">
            {kind === "error" ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                <path d="M12 7v6" />
                <path d="M12 17h.01" />
              </svg>
            ) : kind === "saving" ? (
              <svg className="saveToast__spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                <path d="M20 12a8 8 0 1 1-8-8" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
            )}
          </span>
          <span className="saveToast__label">{label}</span>
        </>
      ) : null}
    </div>,
    document.body,
  );
}
